import apiClient from '@/lib/axios';
import type {
  EstimatorAccount,
  CommissionTransaction,
  CommissionAuditEntry,
  TransactionList,
  RecordDrawRequest,
  TransactionFilters,
} from '../types/commission';

/**
 * Get estimator account summary with balance and recent transactions
 */
export async function getEstimatorAccount(userId: number): Promise<EstimatorAccount> {
  const response = await apiClient.get<EstimatorAccount>(`/commissions/account/${userId}`);
  return response.data;
}

/**
 * Get the current user's estimator account summary
 */
export async function getMyEstimatorAccount(): Promise<EstimatorAccount> {
  const response = await apiClient.get<EstimatorAccount>('/commissions/account/me');
  return response.data;
}

/**
 * Get commission transactions for a user with optional filters
 */
export async function getCommissionTransactions(
  userId: number,
  filters?: TransactionFilters
): Promise<TransactionList> {
  const params = new URLSearchParams();
  params.append('userId', userId.toString());

  if (filters?.startDate) {
    params.append('startDate', filters.startDate);
  }
  if (filters?.endDate) {
    params.append('endDate', filters.endDate);
  }
  if (filters?.transactionType && filters.transactionType !== 'all') {
    params.append('transactionType', filters.transactionType);
  }

  const response = await apiClient.get<TransactionList>(`/commissions?${params.toString()}`);
  return response.data;
}

/**
 * Record a draw (advance payment) for an estimator
 */
export async function recordDraw(request: RecordDrawRequest): Promise<CommissionTransaction> {
  const response = await apiClient.post<CommissionTransaction>('/commissions/draw', request);
  return response.data;
}

/**
 * Get list of estimators (users with Estimator role)
 */
export async function getEstimators(): Promise<{ id: number; name: string }[]> {
  const response = await apiClient.get<{ id: number; name: string }[]>('/users?role=Estimator');
  return response.data;
}

/**
 * Set the reconcile date for a user's commission account
 * This "freezes" the account - commissions before this date are considered settled
 */
export async function setReconcileDate(
  userId: number,
  reconcileDate: string
): Promise<void> {
  await apiClient.post(`/commissions/account/${userId}/reconcile`, {
    reconcileDate,
  });
}

/**
 * Get audit history for a user's commission account
 */
export async function getAuditHistory(
  userId: number,
  startDate?: string,
  endDate?: string
): Promise<CommissionAuditEntry[]> {
  const params = new URLSearchParams();
  if (startDate) {
    params.append('startDate', startDate);
  }
  if (endDate) {
    params.append('endDate', endDate);
  }
  const query = params.toString();
  const url = `/commissions/account/${userId}/audit-history${query ? `?${query}` : ''}`;
  const response = await apiClient.get<CommissionAuditEntry[]>(url);
  return response.data;
}
